/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListWorkloadPlatformsRequest,
  ListWorkloadPlatformsSecurity,
} from "../models/operations/listworkloadplatforms.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildWorkloadPlatformsQuery,
  prefetchWorkloadPlatforms,
  queryKeyWorkloadPlatforms,
  WorkloadPlatformsQueryData,
} from "./workloadPlatforms.core.js";
export {
  buildWorkloadPlatformsQuery,
  prefetchWorkloadPlatforms,
  queryKeyWorkloadPlatforms,
  type WorkloadPlatformsQueryData,
};

export type WorkloadPlatformsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listPlatforms workloadIdentities
 *
 * @remarks
 * List the platforms the catalog offers to trust without looking anything up, each with the guided setup that connects it. The same for every organization. Requires workload:read.
 */
export function useWorkloadPlatforms(
  request?: ListWorkloadPlatformsRequest | undefined,
  security?: ListWorkloadPlatformsSecurity | undefined,
  options?: QueryHookOptions<
    WorkloadPlatformsQueryData,
    WorkloadPlatformsQueryError
  >,
): UseQueryResult<WorkloadPlatformsQueryData, WorkloadPlatformsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildWorkloadPlatformsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listPlatforms workloadIdentities
 *
 * @remarks
 * List the platforms the catalog offers to trust without looking anything up, each with the guided setup that connects it. The same for every organization. Requires workload:read.
 */
export function useWorkloadPlatformsSuspense(
  request?: ListWorkloadPlatformsRequest | undefined,
  security?: ListWorkloadPlatformsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    WorkloadPlatformsQueryData,
    WorkloadPlatformsQueryError
  >,
): UseSuspenseQueryResult<
  WorkloadPlatformsQueryData,
  WorkloadPlatformsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildWorkloadPlatformsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setWorkloadPlatformsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: WorkloadPlatformsQueryData,
): WorkloadPlatformsQueryData | undefined {
  const key = queryKeyWorkloadPlatforms(...queryKeyBase);

  return client.setQueryData<WorkloadPlatformsQueryData>(key, data);
}

export function invalidateWorkloadPlatforms(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "workloadIdentities",
      "listPlatforms",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllWorkloadPlatforms(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "workloadIdentities", "listPlatforms"],
  });
}
